import { useCallback, useEffect, useMemo, useRef } from 'react'

export function debounceAsync<T, Callback extends (...args: any[]) => Promise<T>>(
  callback: Callback,
  wait: number
): (...args: Parameters<Callback>) => Promise<T> {
  let timeoutId: NodeJS.Timeout | null = null

  return (...args: any[]) => {
    if (timeoutId) {
      clearTimeout(timeoutId)
    }

    return new Promise<T>((resolve) => {
      const timeoutPromise = new Promise<void>((resolve) => {
        timeoutId = setTimeout(resolve, wait)
      })
      timeoutPromise.then(async () => {
        resolve(await callback(...args))
      })
    })
  }
}

type CallbackFunction = (...args: any[]) => Promise<unknown>

export const useDebounceAsync = <T extends CallbackFunction>(callback: T, wait = 1200): T => {
  const ref = useRef<T>()

  useEffect(() => {
    ref.current = callback
  }, [callback])

  const debouncedCallback = useMemo(() => {
    const func = async (...args: Parameters<T>) => {
      return ref.current?.(...args)
    }

    return debounceAsync(func, wait) as unknown as T
  }, [wait])

  return useCallback(debouncedCallback, [debouncedCallback])
}
